/**
 * Extraction logic - collects threads by sender, downloads, streams to TAR.
 * Threads are deduplicated: if a thread matches multiple senders, it goes to the first.
 * Files stored under senderDir/threadId/msgId.
 */

import type { GetAccessToken } from './gmailApi';
import {
  createGmailClient,
  findAttachments,
  decodeBase64url,
} from './gmailApi';
import { createTarStream, type TarEntry } from './tarStream';
import { reportError } from './errorReporter';

export interface Phase1Progress {
  phase: 1;
  sender: string;
  senderIndex: number;
  senderCount: number;
  status: 'searching' | 'done' | 'error';
  messageCount?: number;
  error?: string;
  /** Threads scanned so far (during search). */
  scannedCount?: number;
  /** Gmail API resultSizeEstimate, if available. */
  estimatedTotal?: number;
  /** 0–100 when estimatedTotal is available. */
  percent?: number;
}

export interface Phase2Progress {
  phase: 2;
  completed: number;
  failed: number;
  total: number;
  elapsed: number;
  eta: number;
  percent: number;
  completedPerSender: Record<string, number>;
}

export type ProgressCallback = (
  data: Phase1Progress | Phase2Progress
) => void;

export type TarStreamReadyCallback = (stream: ReadableStream<Uint8Array>) => void;

interface WorkItem {
  threadId: string;
  senderDir: string;
}

export async function runExtraction(
  senders: string[],
  maxParallel: number,
  dateAfter: string,
  dateBefore: string,
  getAccessToken: GetAccessToken,
  onProgress: ProgressCallback,
  onTarStreamReady: TarStreamReadyCallback
): Promise<{
  completed: number;
  failed: number;
  total: number;
  senderCounts: Record<string, number>;
}> {
  const gmail = createGmailClient(getAccessToken);
  const senderCounts: Record<string, number> = {};
  const workItems: WorkItem[] = [];
  const senderDirToSender: Record<string, string> = {};
  const assignedThreadIds = new Set<string>();

  const ALL_LABEL = 'All inbox';
  const searchTargets = senders.length > 0 ? senders : [ALL_LABEL];

  for (let i = 0; i < searchTargets.length; i++) {
    const sender = searchTargets[i];
    const safeName =
      sender === ALL_LABEL
        ? 'all'
        : (() => {
            let name = sender
              .replace(/[^a-zA-Z0-9@._-]/g, '_')
              .replace(/@/g, '_at_')
              .replace(/\./g, '_');
            if (name in senderDirToSender) {
              let suffix = 2;
              while (`${name}_${suffix}` in senderDirToSender) suffix++;
              name = `${name}_${suffix}`;
            }
            return name;
          })();
    senderDirToSender[safeName] = sender;

    onProgress({
      phase: 1,
      sender,
      senderIndex: i,
      senderCount: searchTargets.length,
      status: 'searching',
    });

    try {
      let query =
        sender === ALL_LABEL
          ? `(in:inbox OR in:sent) -in:trash -in:spam`
          : `(from:${sender} OR to:${sender} OR cc:${sender} OR bcc:${sender}) (in:inbox OR in:sent) -in:trash -in:spam`;
      if (dateAfter) query += ` after:${dateAfter}`;
      if (dateBefore) query += ` before:${dateBefore}`;

      let initialEstimate: number | undefined;
      if (sender === ALL_LABEL && !dateAfter && !dateBefore) {
        try {
          const [inbox, sent] = await Promise.all([
            gmail.getLabel('INBOX'),
            gmail.getLabel('SENT'),
          ]);
          const inboxThreads = inbox.threadsTotal ?? 0;
          const sentThreads = sent.threadsTotal ?? 0;
          initialEstimate = Math.max(inboxThreads, sentThreads);
        } catch {
          /* fall back to resultSizeEstimate from API */
        }
      }

      const threads = await gmail.searchThreads(
        query,
        (scanned, estimated) => {
          const percent =
            estimated != null && estimated > 0
              ? Math.min(100, Math.round((scanned / estimated) * 100))
              : undefined;
          onProgress({
            phase: 1,
            sender,
            senderIndex: i,
            senderCount: searchTargets.length,
            status: 'searching',
            scannedCount: scanned,
            estimatedTotal: estimated,
            percent,
          });
        },
        initialEstimate
      );

      let threadCount = 0;
      for (const thread of threads) {
        const tid = thread.id;
        if (!tid || assignedThreadIds.has(tid)) continue;
        assignedThreadIds.add(tid);
        workItems.push({ threadId: tid, senderDir: safeName });
        threadCount++;
      }

      senderCounts[sender] = threadCount;

      onProgress({
        phase: 1,
        sender,
        senderIndex: i,
        senderCount: searchTargets.length,
        messageCount: threadCount,
        status: 'done',
      });
    } catch (e) {
      senderCounts[sender] = 0;
      onProgress({
        phase: 1,
        sender,
        senderIndex: i,
        senderCount: searchTargets.length,
        messageCount: 0,
        status: 'error',
        error: e instanceof Error ? e.message : 'unknown',
      });
    }
  }

  if (workItems.length === 0) {
    return { completed: 0, failed: 0, total: 0, senderCounts };
  }

  const tar = createTarStream();
  onTarStreamReady(tar.stream);

  let completed = 0;
  let failed = 0;
  const total = workItems.length;
  const startTime = Date.now();
  const queue = [...workItems];
  const completedPerSender: Record<string, number> = {};
  for (const s of Object.values(senderDirToSender)) {
    completedPerSender[s] = 0;
  }

  async function downloadOne(item: WorkItem): Promise<void> {
    const entries: TarEntry[] = [];

    try {
      const thread = await gmail.getThread(item.threadId, 'full');
      const messages = thread.messages || [];

      for (const msg of messages) {
        const msgId = msg.id;
        if (!msgId) continue;

        try {
          const rawMsg = await gmail.getMessageRaw(msgId);
          if (rawMsg.raw) {
            const decoded = decodeBase64url(rawMsg.raw);
            entries.push({
              name: `${item.senderDir}/${item.threadId}/${msgId}.eml`,
              data: decoded,
            });
          }
        } catch (e) {
          reportError('Raw message download failed', { threadId: item.threadId, msgId }, e);
        }

        const jsonStr = JSON.stringify(msg, null, 2);
        entries.push({
          name: `${item.senderDir}/${item.threadId}/${msgId}.json`,
          data: new TextEncoder().encode(jsonStr),
        });

        const attachments = findAttachments(msg.payload);
        const usedNames = new Set<string>();
        for (const att of attachments) {
          try {
            let safeName = att.filename.replace(/[/\\:*?"<>|]/g, '_');
            if (usedNames.has(safeName)) {
              const dot = safeName.lastIndexOf('.');
              const base = dot > 0 ? safeName.slice(0, dot) : safeName;
              const ext = dot > 0 ? safeName.slice(dot) : '';
              let suffix = 2;
              while (usedNames.has(`${base}_${suffix}${ext}`)) suffix++;
              safeName = `${base}_${suffix}${ext}`;
            }
            usedNames.add(safeName);
            let data: string | undefined;
            if (att.inlineData) {
              data = att.inlineData;
            } else if (att.attachmentId) {
              const attData = await gmail.getAttachment(msgId, att.attachmentId);
              data = attData.data;
            }
            if (data) {
              const decoded = decodeBase64url(data);
              entries.push({
                name: `${item.senderDir}/${item.threadId}/${msgId}_attachments/${safeName}`,
                data: decoded,
              });
            }
          } catch (e) {
            reportError('Attachment download failed', { threadId: item.threadId, msgId, filename: att.filename }, e);
          }
        }
      }

      completed++;
      completedPerSender[senderDirToSender[item.senderDir]] =
        (completedPerSender[senderDirToSender[item.senderDir]] ?? 0) + 1;

      tar.pushEntries(entries);
    } catch (e) {
      failed++;
      reportError('Thread download failed', { threadId: item.threadId, senderDir: item.senderDir, entryNames: entries.map(e => e.name) }, e);
    }

    const elapsed = Date.now() - startTime;
    const done = completed + failed;
    const rate = done / (elapsed / 1000);
    const remaining = total - done;
    const eta = rate > 0 ? Math.ceil(remaining / rate) : 0;

    onProgress({
      phase: 2,
      completed,
      failed,
      total,
      elapsed,
      eta,
      percent: Math.round((done / total) * 100),
      completedPerSender: { ...completedPerSender },
    });
  }

  const workers = Array.from({ length: maxParallel }, async () => {
    while (queue.length > 0) {
      const item = queue.shift();
      if (item) await downloadOne(item);
    }
  });
  await Promise.all(workers);

  tar.close();

  return {
    completed,
    failed,
    total,
    senderCounts,
  };
}
